import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardBody,
  SegmentedControl,
  cn,
  useToast,
} from '@nexply/ui';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ContactRound,
  FileUp,
  RotateCcw,
  Smartphone,
  X,
} from 'lucide-react';
import { contactFingerprint, parseVCards, type VCardContact } from '@nexply/core';
import type { MemberCreateInput, MemberDTO } from '@nexply/api-client';
import { api } from '../lib/api.js';
import { invalidateMemberViews } from '../lib/queries.js';
import { fileToCompressedDataUrl } from '../lib/image.js';
import { ContactSwipeCard, type SwipeDir } from './ContactSwipeCard.js';
import { ContactEnrichSheet, type EnrichResult } from './ContactEnrichSheet.js';

type Step = 'source' | 'prepare' | 'swipe' | 'done';
type Noise = 'all' | 'contactable' | 'company';

/** Contact Picker API — nur Chrome/Android. Kein DOM-Typ vorhanden, deshalb hier minimal deklariert. */
interface PickedContact {
  name?: string[];
  email?: string[];
  tel?: string[];
}
interface ContactsManagerLike {
  select(props: string[], opts?: { multiple?: boolean }): Promise<PickedContact[]>;
}

function contactPicker(): ContactsManagerLike | null {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return null;
  if (!('contacts' in navigator) || !('ContactsManager' in window)) return null;
  return (navigator as Navigator & { contacts?: ContactsManagerLike }).contacts ?? null;
}

/** Beide Quellen (vcf + Picker) münden in dieselbe Struktur — danach gibt es nur noch eine Pipeline. */
function fromPicked(p: PickedContact): VCardContact | null {
  const name = (p.name ?? []).map((n) => n.trim()).filter(Boolean)[0] ?? '';
  const emails = (p.email ?? []).map((e) => e.trim()).filter(Boolean);
  const phones = (p.tel ?? []).map((t) => t.trim()).filter(Boolean);
  if (!name && emails.length === 0) return null;
  return {
    name: name || emails[0]!,
    email: emails[0],
    phone: phones[0],
    allEmails: emails,
    allPhones: phones,
  };
}

function normPhone(v: string | null | undefined): string {
  return (v ?? '').replace(/[^\d]/g, '').slice(-9);
}

function nameKey(name: string, company?: string | null): string {
  return `${name.trim().toLowerCase()}|${(company ?? '').trim().toLowerCase()}`;
}

/** Schon im Netzwerk? E-Mail schlägt Telefon schlägt Name+Firma. */
function buildExistingMatcher(members: MemberDTO[]) {
  const emails = new Set<string>();
  const phones = new Set<string>();
  const names = new Set<string>();
  for (const m of members) {
    if (m.email) emails.add(m.email.trim().toLowerCase());
    const p = normPhone(m.phone);
    if (p.length >= 6) phones.add(p);
    names.add(nameKey(m.name, m.company));
  }
  return (c: VCardContact): boolean => {
    if (c.email && emails.has(c.email.trim().toLowerCase())) return true;
    const p = normPhone(c.phone);
    if (p.length >= 6 && phones.has(p)) return true;
    return names.has(nameKey(c.name, c.company));
  };
}

/**
 * vCard-Fotos sind rohes Base64 aus dem Handy und sprengen leicht das 2-MB-Limit des
 * Contracts. Erst herunterrechnen, und wenn irgendetwas schiefgeht: Foto weglassen —
 * ein Kontakt darf nie an seinem Bild scheitern.
 */
const PHOTO_MAX_CHARS = 2_000_000;

async function shrinkPhoto(dataUrl: string | undefined): Promise<string | null> {
  if (!dataUrl || !dataUrl.startsWith('data:')) return null;
  try {
    const blob = await (await fetch(dataUrl)).blob();
    const file = new File([blob], 'contact.jpg', { type: 'image/jpeg' });
    const small = await fileToCompressedDataUrl(file, 384, 0.8);
    return small.length <= PHOTO_MAX_CHARS ? small : null;
  } catch {
    return null;
  }
}

interface LastAction {
  kind: 'accept' | 'discard';
  contact: VCardContact;
  memberId?: string;
}

export function ContactsImportWizard({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();

  const [step, setStep] = useState<Step>('source');
  const [parsed, setParsed] = useState<VCardContact[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [noise, setNoise] = useState<Noise>('all');

  // Der Stapel wird beim Start eingefroren — ein Filterwechsel mitten im Swipen
  // würde die Indizes verschieben und Karten überspringen.
  const [deck, setDeck] = useState<VCardContact[]>([]);
  const [index, setIndex] = useState(0);
  const [fly, setFly] = useState<SwipeDir | null>(null);

  const [accepted, setAccepted] = useState<VCardContact[]>([]);
  const [discarded, setDiscarded] = useState<VCardContact[]>([]);
  const [last, setLast] = useState<LastAction | null>(null);

  const [pendingAccept, setPendingAccept] = useState<VCardContact | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);

  const dismissedQ = useQuery({
    queryKey: ['members', 'dismissed'],
    queryFn: () => api.members.listDismissed(),
  });
  // Die Dublettenprüfung braucht *alle* Mitglieder — mit limit=200 (Contract-Maximum) würden
  // größere Netzwerke ab dem 201. Mitglied stumm wieder als Dublette importiert.
  // Eigener Query-Key (nicht unter 'members'): die Liste wird beim Anlegen jedes übernommenen
  // Kontakts sonst invalidiert und komplett neu durchgeblättert.
  const membersQ = useQuery({
    queryKey: ['contacts-import', 'existing-members'],
    staleTime: Infinity,
    queryFn: async () => {
      const all: MemberDTO[] = [];
      for (let offset = 0; offset < 5000; offset += 200) {
        const page = await api.members.list({ limit: 200, offset });
        all.push(...page.members);
        if (all.length >= page.total || page.members.length === 0) break;
      }
      return all;
    },
  });

  const dismissedSet = useMemo(
    () => new Set(dismissedQ.data?.fingerprints ?? []),
    [dismissedQ.data],
  );
  const isExisting = useMemo(() => buildExistingMatcher(membersQ.data ?? []), [membersQ.data]);

  // Vorfilter: bereits weggeswipt (unsichtbar) und bereits im Netzwerk (mit Zähler sichtbar).
  const triage = useMemo(() => {
    const alreadyDismissed: VCardContact[] = [];
    const alreadyInNetwork: VCardContact[] = [];
    const fresh: VCardContact[] = [];
    for (const c of parsed) {
      if (dismissedSet.has(contactFingerprint(c))) alreadyDismissed.push(c);
      else if (isExisting(c)) alreadyInNetwork.push(c);
      else fresh.push(c);
    }
    return { alreadyDismissed, alreadyInNetwork, fresh };
  }, [parsed, dismissedSet, isExisting]);

  const candidates = useMemo(() => {
    if (noise === 'company') return triage.fresh.filter((c) => !!c.company);
    if (noise === 'contactable') return triage.fresh.filter((c) => !!c.company || !!c.email);
    return triage.fresh;
  }, [triage.fresh, noise]);

  // Bei großen Exporten ist "alles zeigen" die falsche Vorgabe — 500 Karten swipt niemand.
  const suggestionApplied = useRef(false);
  useEffect(() => {
    if (suggestionApplied.current || step !== 'prepare') return;
    if (triage.fresh.length > 60) {
      setNoise('contactable');
      suggestionApplied.current = true;
    }
  }, [step, triage.fresh.length]);

  const current = deck[index] ?? null;
  const dismissMut = useMutation({
    mutationFn: (fps: string[]) => api.members.dismiss(fps),
  });

  function loadText(text: string) {
    const res = parseVCards(text);
    setParsed(res.contacts);
    setParseErrors(res.errors);
    if (res.contacts.length > 0) setStep('prepare');
  }

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.currentTarget.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => loadText(String(reader.result ?? ''));
    reader.onerror = () => setParseErrors(['Datei konnte nicht gelesen werden.']);
    // Umlaute in iPhone-/Android-Exporten kommen sonst als Kauderwelsch an.
    reader.readAsText(f, 'utf-8');
    e.currentTarget.value = '';
  }

  async function pickFromPhone() {
    const cm = contactPicker();
    if (!cm) return;
    try {
      const picked = await cm.select(['name', 'email', 'tel'], { multiple: true });
      const contacts = picked.map(fromPicked).filter((c): c is VCardContact => c !== null);
      if (contacts.length === 0) {
        setParseErrors(['Keine Kontakte ausgewählt.']);
        return;
      }
      setParsed(contacts);
      setParseErrors([]);
      setStep('prepare');
    } catch {
      setParseErrors(['Die Kontakte-App hat den Zugriff abgelehnt. Nutze die .vcf-Datei.']);
    }
  }

  function start() {
    finishing.current = false;
    setDeck(candidates);
    setIndex(0);
    setAccepted([]);
    setDiscarded([]);
    setLast(null);
    setStep('swipe');
  }

  const handleExit = useCallback(
    (dir: SwipeDir) => {
      const c = deck[index];
      setFly(null);
      if (!c) return;
      setIndex((i) => i + 1);
      if (dir === 'right') {
        setCreateError(null);
        setPendingAccept(c);
      } else {
        setDiscarded((d) => [...d, c]);
        setLast({ kind: 'discard', contact: c });
      }
    },
    [deck, index],
  );

  const createMut = useMutation({
    mutationFn: async (input: MemberCreateInput) => api.members.create(input),
  });

  async function confirmAccept(res: EnrichResult) {
    if (!pendingAccept) return;
    const contact = pendingAccept;
    setCreateError(null);
    const photoUrl = await shrinkPhoto(contact.photoUrl);
    try {
      const { member } = await createMut.mutateAsync({ ...res.input, photoUrl });
      setAccepted((a) => [...a, contact]);
      setLast({ kind: 'accept', contact, memberId: member.id });
      setPendingAccept(null);
      invalidateMemberViews(qc);
      // Die Kanten brauchen die frische Member-ID — erst hier ist sie bekannt. Eine
      // fehlgeschlagene Kante darf den bereits angelegten Kontakt nicht zurücknehmen.
      void linkAll(member.id, res.links);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Anlegen fehlgeschlagen.');
    }
  }

  async function linkAll(memberId: string, links: EnrichResult['links']) {
    if (links.length === 0) return;
    let failed = 0;
    for (const l of links) {
      try {
        await api.network.createRelationship({
          aId: memberId,
          bId: l.memberId,
          type: l.type,
          strength: 50,
          hidden: false,
          note: l.reason,
        });
      } catch {
        failed++;
      }
    }
    const ok = links.length - failed;
    if (ok > 0) {
      toast.show(
        `${ok} ${ok === 1 ? 'Verbindung' : 'Verbindungen'} angelegt.`,
        'success',
      );
    }
    if (failed > 0) {
      toast.show(
        `${failed} ${failed === 1 ? 'Verbindung' : 'Verbindungen'} konnten nicht angelegt werden.`,
        'error',
      );
    }
  }

  /** Abbrechen im Sheet zählt weder als übernommen noch als verworfen — die Karte kommt zurück. */
  function cancelAccept() {
    setPendingAccept(null);
    setCreateError(null);
    setIndex((i) => Math.max(0, i - 1));
  }

  async function undo() {
    if (!last) return;
    if (last.kind === 'discard') {
      setDiscarded((d) => d.filter((c) => c !== last.contact));
    } else {
      try {
        if (last.memberId) await api.members.remove(last.memberId);
        setAccepted((a) => a.filter((c) => c !== last.contact));
        invalidateMemberViews(qc);
      } catch {
        toast.show('Rückgängig fehlgeschlagen — der Kontakt bleibt im Netzwerk.', 'error');
        return;
      }
    }
    setIndex((i) => Math.max(0, i - 1));
    setLast(null);
  }

  const finishing = useRef(false);

  async function finish() {
    if (finishing.current) return;
    finishing.current = true;
    const fps = Array.from(new Set(discarded.map((c) => contactFingerprint(c))));
    if (fps.length > 0) {
      try {
        await dismissMut.mutateAsync(fps);
        qc.invalidateQueries({ queryKey: ['members', 'dismissed'] });
      } catch {
        toast.show('Verworfene Kontakte konnten nicht gemerkt werden.', 'error');
      }
    }
    setStep('done');
  }

  // Der Stapel ist durch → automatisch abschließen.
  useEffect(() => {
    if (step === 'swipe' && deck.length > 0 && index >= deck.length && !pendingAccept) {
      void finish();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, index, deck.length, pendingAccept]);

  // Tastatur nur, solange wirklich geswipt wird und kein Feld/Sheet den Fokus hat.
  useEffect(() => {
    if (step !== 'swipe' || pendingAccept || !current || fly) return;
    function onKey(e: KeyboardEvent) {
      const el = document.activeElement;
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setFly('left');
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setFly('right');
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step, pendingAccept, current, fly]);

  const skipped = Math.max(0, deck.length - accepted.length - discarded.length);

  return (
    <div className="space-y-6">
      {step === 'source' && (
        <SourceStep
          errors={parseErrors}
          onFile={onFile}
          picker={contactPicker() !== null}
          onPick={pickFromPhone}
        />
      )}

      {/* Teil-Fehler (einzelne kaputte Einträge) dürfen nicht verschwinden, nur weil der Rest lesbar war. */}
      {step === 'prepare' && parseErrors.length > 0 && <ParseErrors errors={parseErrors} />}

      {step === 'prepare' && (
        <PrepareStep
          total={parsed.length}
          dismissed={triage.alreadyDismissed.length}
          inNetwork={triage.alreadyInNetwork.length}
          count={candidates.length}
          noise={noise}
          onNoise={setNoise}
          loading={dismissedQ.isLoading || membersQ.isLoading}
          onStart={start}
          onBack={() => {
            setStep('source');
            setParsed([]);
            setParseErrors([]);
          }}
        />
      )}

      {step === 'swipe' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-text-dim">
              {Math.min(index + 1, deck.length)} von {deck.length}
            </span>
            <span className="text-text-muted">
              <span className="text-success">{accepted.length}</span> ·{' '}
              <span className="text-danger">{discarded.length}</span>
            </span>
          </div>
          <div className="h-1 w-full overflow-hidden rounded-full bg-raise-2">
            <div
              className="h-full rounded-full bg-gold transition-all"
              style={{ width: `${deck.length ? (index / deck.length) * 100 : 0}%` }}
            />
          </div>

          <div className="relative mx-auto h-[440px] w-full max-w-sm">
            {deck
              .slice(index, index + 3)
              .map((c, i) => ({ c, i }))
              .reverse()
              .map(({ c, i }) =>
                i === 0 ? (
                  <ContactSwipeCard
                    key={contactFingerprint(c) + index}
                    contact={c}
                    fly={fly}
                    onExit={handleExit}
                  />
                ) : (
                  <div
                    key={contactFingerprint(c) + i}
                    className="absolute inset-0 rounded-3xl border border-line bg-surface-2"
                    style={{
                      transform: `scale(${1 - i * 0.04}) translateY(${i * 10}px)`,
                      opacity: 1 - i * 0.35,
                    }}
                  />
                ),
              )}
            {!current && (
              <div className="grid h-full place-items-center rounded-3xl border border-dashed border-line text-sm text-text-muted">
                Stapel leer.
              </div>
            )}
          </div>

          <div className="flex items-center justify-center gap-4 pt-1">
            <button
              onClick={() => setFly('left')}
              disabled={!current || !!fly}
              aria-label="Verwerfen"
              className="grid h-16 w-16 place-items-center rounded-full border-2 border-danger/40 bg-danger/10 text-danger active:scale-95 transition disabled:opacity-40"
            >
              <X className="h-7 w-7" strokeWidth={2.5} />
            </button>
            <button
              onClick={() => void undo()}
              disabled={!last}
              aria-label="Rückgängig"
              className="grid h-12 w-12 place-items-center rounded-full border border-line bg-surface-3 text-text-dim active:scale-95 transition disabled:opacity-30"
            >
              <RotateCcw className="h-5 w-5" />
            </button>
            <button
              onClick={() => setFly('right')}
              disabled={!current || !!fly}
              aria-label="Ins Netzwerk übernehmen"
              className="grid h-16 w-16 place-items-center rounded-full border-2 border-success/40 bg-success/10 text-success active:scale-95 transition disabled:opacity-40"
            >
              <Check className="h-7 w-7" strokeWidth={2.5} />
            </button>
          </div>

          <div className="flex items-center justify-center gap-4 text-[11px] text-text-muted">
            <span className="inline-flex items-center gap-1">
              <ArrowLeft className="h-3.5 w-3.5" /> verwerfen
            </span>
            <span className="inline-flex items-center gap-1">
              übernehmen <ArrowRight className="h-3.5 w-3.5" />
            </span>
            <span>· Rückgängig macht den letzten Swipe zurück</span>
          </div>

          <div className="flex justify-center pt-2">
            <Button variant="ghost" size="sm" onClick={() => void finish()}>
              Abbrechen und abschließen
            </Button>
          </div>
        </div>
      )}

      {step === 'done' && (
        <Card>
          <CardBody className="space-y-5 text-center">
            <div className="text-lg font-semibold text-text">Fertig.</div>
            <div className="grid grid-cols-3 gap-3">
              <Summary label="übernommen" value={accepted.length} tone="success" />
              <Summary label="verworfen" value={discarded.length} tone="danger" />
              <Summary label="übersprungen" value={skipped} tone="muted" />
            </div>
            <p className="text-xs text-text-muted">
              Verworfene Kontakte tauchen beim nächsten Import nicht wieder auf.
            </p>
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
              <Button fullWidth onClick={onClose}>Zu den Mitgliedern</Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setStep('source');
                  setParsed([]);
                  setParseErrors([]);
                  setDeck([]);
                  setIndex(0);
                  suggestionApplied.current = false;
                }}
              >
                Weitere Datei
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      <ContactEnrichSheet
        open={!!pendingAccept}
        contact={pendingAccept}
        pending={createMut.isPending}
        error={createError}
        onCancel={cancelAccept}
        onSubmit={(res) => void confirmAccept(res)}
      />
    </div>
  );
}

function Summary({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'success' | 'danger' | 'muted';
}) {
  return (
    <div className="rounded-2xl border border-line bg-raise p-4">
      <div
        className={cn(
          'text-2xl font-semibold',
          tone === 'success' && 'text-success',
          tone === 'danger' && 'text-danger',
          tone === 'muted' && 'text-text-dim',
        )}
      >
        {value}
      </div>
      <div className="mt-1 text-xs text-text-muted">{label}</div>
    </div>
  );
}

function ParseErrors({ errors }: { errors: string[] }) {
  return (
    <div className="space-y-1 rounded-xl border border-danger/30 bg-danger/10 p-3 text-xs text-text-dim">
      {errors.slice(0, 8).map((e, i) => (
        <div key={i}>
          <span className="text-danger">Hinweis:</span> {e}
        </div>
      ))}
      {errors.length > 8 && (
        <div className="text-text-muted">… und {errors.length - 8} weitere.</div>
      )}
    </div>
  );
}

function SourceStep({
  errors,
  onFile,
  picker,
  onPick,
}: {
  errors: string[];
  onFile: (e: React.ChangeEvent<HTMLInputElement>) => void;
  picker: boolean;
  onPick: () => void;
}) {
  return (
    <div className="space-y-4">
      {errors.length > 0 && <ParseErrors errors={errors} />}

      <Card>
        <CardBody className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-gold/10 text-gold">
              <FileUp className="h-5 w-5" />
            </div>
            <div>
              <div className="text-[15px] font-semibold text-text">vCard-Datei (.vcf)</div>
              <div className="text-xs text-text-dim">Funktioniert auf iPhone und Android.</div>
            </div>
          </div>

          <label className="block">
            <input
              type="file"
              accept=".vcf,text/vcard,text/x-vcard"
              onChange={onFile}
              className="block w-full text-xs text-text-dim file:mr-3 file:h-11 file:rounded-xl file:border-0 file:bg-raise-2 file:px-4 file:text-sm file:font-medium file:text-text hover:file:bg-raise-3"
            />
          </label>

          <div className="space-y-1.5 rounded-xl bg-raise p-3 text-xs text-text-dim">
            <div className="font-medium text-text">So kommst du an die Datei:</div>
            <div>
              <span className="text-gold-light">iPhone:</span> Kontakte → Listen → alle auswählen →
              Teilen → „In Dateien sichern“.
            </div>
            <div>
              <span className="text-gold-light">Android:</span> Kontakte → Einstellungen →
              Exportieren → .vcf-Datei.
            </div>
          </div>
        </CardBody>
      </Card>

      {picker && (
        <Card>
          <CardBody className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-gold/10 text-gold">
                <ContactRound className="h-5 w-5" />
              </div>
              <div>
                <div className="text-[15px] font-semibold text-text">Kontakte-App</div>
                <div className="text-xs text-text-dim">
                  Direkt auswählen — ohne Datei. Dein Gerät unterstützt das.
                </div>
              </div>
            </div>
            <Button variant="secondary" fullWidth onClick={onPick}>
              <Smartphone className="h-4 w-4" /> Kontakte auswählen
            </Button>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

const NOISE_OPTIONS: { value: Noise; label: string }[] = [
  { value: 'all', label: 'Alle' },
  { value: 'contactable', label: 'Firma o. E-Mail' },
  { value: 'company', label: 'Nur mit Firma' },
];

function PrepareStep({
  total,
  dismissed,
  inNetwork,
  count,
  noise,
  onNoise,
  loading,
  onStart,
  onBack,
}: {
  total: number;
  dismissed: number;
  inNetwork: number;
  count: number;
  noise: Noise;
  onNoise: (n: Noise) => void;
  loading: boolean;
  onStart: () => void;
  onBack: () => void;
}) {
  return (
    <Card>
      <CardBody className="space-y-5">
        <div>
          <div className="text-[15px] font-semibold text-text">{total} Kontakte gelesen</div>
          <div className="mt-2 space-y-1 text-xs text-text-dim">
            {inNetwork > 0 && <div>{inNetwork} sind schon in deinem Netzwerk — ausgeblendet.</div>}
            {dismissed > 0 && (
              <div>{dismissed} hast du früher schon verworfen — ausgeblendet.</div>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <div className="text-xs text-text-muted">
            Ein voller Handy-Export enthält viel Rauschen (Pizzadienst, Zahnarzt). Filtere vorab,
            statt 500 Karten zu swipen.
          </div>
          <SegmentedControl options={NOISE_OPTIONS} value={noise} onChange={onNoise} />
        </div>

        <div className="rounded-2xl border border-gold/25 bg-gold/[0.06] p-4 text-center">
          <div className="text-3xl font-semibold text-gold">{count}</div>
          <div className="mt-1 text-xs text-text-dim">Karten im Stapel</div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row-reverse">
          <Button fullWidth disabled={count === 0 || loading} onClick={onStart}>
            Stapel starten
          </Button>
          <Button variant="ghost" fullWidth onClick={onBack}>
            Andere Datei
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
